import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { API } from "@/lib/api";

const PlayerContext = createContext(null);
const PREVIEW_LIMIT_SECONDS = 30;

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(null); // beat object
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.preload = "metadata";
    const a = audioRef.current;
    const onTime = () => {
      setProgress(a.currentTime || 0);
      // 30s preview cap
      if (a.currentTime >= PREVIEW_LIMIT_SECONDS) {
        a.pause();
        a.currentTime = 0;
        setPlaying(false);
        setProgress(0);
      }
    };
    const onMeta = () => {
      const d = a.duration || 0;
      setDuration(Math.min(d, PREVIEW_LIMIT_SECONDS));
    };
    const onEnded = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnded);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnded);
      a.pause();
    };
  }, []);

  const play = useCallback((beat) => {
    const a = audioRef.current;
    if (!a) return;
    if (current?.id === beat.id) {
      if (a.paused) { a.play(); setPlaying(true); }
      else { a.pause(); setPlaying(false); }
      return;
    }
    setCurrent(beat);
    a.src = `${API}/stream/preview/${beat.id}`;
    a.currentTime = 0;
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  }, [current]);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a || !current) return;
    if (a.paused) { a.play(); setPlaying(true); }
    else { a.pause(); setPlaying(false); }
  }, [current]);

  const seek = useCallback((pct) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const target = Math.min(pct * duration, PREVIEW_LIMIT_SECONDS - 0.1);
    a.currentTime = Math.max(0, target);
  }, [duration]);

  const close = useCallback(() => {
    const a = audioRef.current;
    if (a) { a.pause(); a.src = ""; }
    setCurrent(null);
    setPlaying(false);
  }, []);

  return (
    <PlayerContext.Provider value={{ current, playing, progress, duration, play, toggle, seek, close }}>
      {children}
    </PlayerContext.Provider>
  );
}

export const usePlayer = () => useContext(PlayerContext);
